/**
 * How the supplier-signature card reads a verdict (#44).
 *
 * Svelte-free on purpose, so a test can hold every reason the backend can send
 * against the badge it gets and the Italian it is explained with.
 */

/** Badge class and status label for each reason, and for the two states before a verdict. */
const VIEWS = Object.freeze({
  ok: { badge: 'badge-green', label: 'valid' },
  integrity_only: { badge: 'badge-blue', label: 'integrityOnly' },
  integrity_failed: { badge: 'badge-red', label: 'invalid' },
  // Not an invalid signature: the check failed on a copy another program kept,
  // which may differ from what SdI delivered.
  imported_copy_unverifiable: { badge: 'badge-gray', label: 'importedCopyUnverifiable' },
  untrusted: { badge: 'badge-red', label: 'invalid' },
  expired: { badge: 'badge-yellow', label: 'invalid' },
  not_yet_valid: { badge: 'badge-yellow', label: 'invalid' },
  no_trust_store: { badge: 'badge-gray', label: 'unverified' },
  unreadable: { badge: 'badge-gray', label: 'unverified' },
  check_failed: { badge: 'badge-gray', label: 'unverified' },
  pending: { badge: 'badge-blue', label: 'pending' },
  paused: { badge: 'badge-gray', label: 'paused' },
});

/** Every key the card can look up — reasons, plus the two states with no verdict yet. */
export const SIGNATURE_VIEW_KEYS = Object.freeze(Object.keys(VIEWS));

/**
 * @param {object|null} verification - `verification` of GET …/signature
 * @returns {null|{ key: string, badge: string, labelKey: string, explanationKey: string }}
 */
export function signatureView(verification) {
  if (!verification) return null;
  const key = verification.reason
    ?? (verification.status === 'paused' ? 'paused' : 'pending');
  const view = VIEWS[key];
  if (!view) return null;
  return {
    key,
    badge: view.badge,
    labelKey: `invoices.signatureCheck.status.${view.label}`,
    explanationKey: `invoices.signatureCheck.reasons.${key}`,
  };
}

/**
 * The label of the signer row: proven, claimed by an altered document, or
 * named by a signature that could not be checked on an imported copy.
 */
export function signerLabelKey(verification) {
  const signer = verification?.signer;
  if (!signer?.claimedOnly) return 'invoices.signatureCheck.fields.signer';
  return verification.reason === 'imported_copy_unverifiable'
    ? 'invoices.signatureCheck.fields.importedCopySigner'
    : 'invoices.signatureCheck.fields.claimedSigner';
}

/** The Italian clause naming where the reference time came from, or null. */
export function referenceSourceKey(source) {
  return ['received', 'signing_time', 'imported'].includes(source)
    ? `invoices.signatureCheck.referenceSource.${source}`
    : null;
}
